import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import {
  NotificationApiService,
  NotificationLiveDto,
  NotificationOutDto,
  NotificationType,
} from '@core/api/notification-api.service';
import {
  NotifCategory,
  categoryIcon,
  classifyNotification,
  isLoginWelcomeNotification,
} from '@core/config/notification-routing.config';
import { AuthService } from '@core/services/auth.service';
import { BackendI18nService } from '@core/services/backend-i18n.service';
import { NotificationsSignalRService } from '@core/services/notifications-signalr.service';
import { WorkspaceService } from '@core/services/workspace.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, firstValueFrom, of, Subscription } from 'rxjs';

export type NotifIconClass = 'alert' | 'campaign' | 'ai' | 'success' | 'system';

/** Shared shape for the topbar dropdown and the full notifications page. */
export interface AppNotificationItem {
  id: number;
  icon: string;
  iconClass: NotifIconClass;
  title: string;
  preview: string;
  time: string;
  unread: boolean;
  /** What the notification is about — drives the inbox filters and its links. */
  category: NotifCategory;
  detailType: string;
  detailTime: string;
  body: string;
  createdAt: string | null;
  type: NotificationType;
  /** Workspace it belongs to; null = personal / platform-wide (shown everywhere). */
  organizationId: number | null;
  /** Untranslated title, so routing rules can match the backend's English text. */
  rawTitle: string;
  /** Chat thread to open; null for non-chat notifications. */
  conversationId: number | null;
}

/**
 * A notification as the backend sent it. Title/message stay in their original
 * (English, possibly `[bracketed]`) form so they can be re-translated whenever
 * the active language changes.
 */
interface RawNotification {
  id: number;
  title: string;
  message: string;
  createdAt: string | null;
  type: NotificationType;
  unread: boolean;
  /** Workspace the item belongs to; null = personal / platform-wide. */
  organizationId: number | null;
  conversationId: number | null;
}

const READ_KEY_PREFIX = 'notif_read_ids_';
const DROPDOWN_LIMIT = 20;

/**
 * Loads in-app notifications from the existing Notification microservice
 * (GET api/notification/all + SignalR /notification) and exposes a shared
 * signal store for the dashboard bell and the notifications page.
 *
 * Read/unread is client-side only — the backend Status field is delivery state.
 *
 * The inbox is workspace-scoped: the API returns items raised in the active
 * workspace plus personal / platform-wide ones (billing, account), and
 * the store reloads whenever the user switches workspace. Live SignalR pushes
 * are filtered the same way so a notification from another workspace never
 * appears in the bell. Sign-in greetings are dropped — they are not inbox items.
 *
 * The service is not localized, so titles/bodies arrive in English and are
 * translated on the client via {@link BackendI18nService} (`NOTIFICATION`
 * bucket). Presentation is derived rather than stored so switching language
 * re-translates the list in place.
 */
@Injectable({ providedIn: 'root' })
export class AppNotificationsService {
  private readonly api = inject(NotificationApiService);
  private readonly signalr = inject(NotificationsSignalRService);
  private readonly auth = inject(AuthService);
  private readonly backendI18n = inject(BackendI18nService);
  private readonly translate = inject(TranslateService);
  private readonly workspace = inject(WorkspaceService);

  private liveSub: Subscription | null = null;
  private langSub: Subscription | null = null;
  private signalrRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private tempIdSeq = -1;
  /** Workspace the loaded list belongs to, so a switch triggers exactly one reload. */
  private loadedForOrgId: number | null = null;

  private readonly raw = signal<RawNotification[]>([]);
  /** Bumped on language change so the presented list recomputes. */
  private readonly langTick = signal(0);

  readonly loading = signal(false);
  readonly error = signal(false);

  readonly items = computed<AppNotificationItem[]>(() => {
    this.langTick();
    return this.inboxRaw().map((n) => this.present(n));
  });

  readonly unreadCount = computed(
    () => this.inboxRaw().filter((n) => n.unread).length,
  );

  /** Newest items for the topbar dropdown. */
  readonly dropdownItems = computed(() => this.items().slice(0, DROPDOWN_LIMIT));

  constructor() {
    // Switching workspace swaps the inbox: drop the previous workspace's items
    // immediately, then reload scoped to the new one.
    effect(() => {
      const orgId = this.activeOrgId();
      if (!this.started) return;
      untracked(() => {
        if (orgId === this.loadedForOrgId) return;
        this.raw.set([]);
        void this.refresh();
      });
    });
  }

  /** Call once when the authenticated shell mounts. */
  start(): void {
    if (this.started) return;
    this.started = true;

    void this.refresh();
    this.connectSignalr();

    this.liveSub = this.signalr.notifications$.subscribe((live) => {
      this.prependLive(live);
    });

    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.langTick.update((n) => n + 1);
    });
  }

  stop(): void {
    if (this.signalrRetryTimer !== null) {
      clearTimeout(this.signalrRetryTimer);
      this.signalrRetryTimer = null;
    }
    this.liveSub?.unsubscribe();
    this.liveSub = null;
    this.langSub?.unsubscribe();
    this.langSub = null;
    this.started = false;
    this.loadedForOrgId = null;
    this.raw.set([]);
    void this.signalr.stop().catch(() => undefined);
  }

  private connectSignalr(): void {
    if (!this.started) return;

    void this.signalr.ensureConnected().catch(() => {
      if (!this.started || this.signalrRetryTimer !== null) return;
      this.signalrRetryTimer = setTimeout(() => {
        this.signalrRetryTimer = null;
        this.connectSignalr();
      }, 5_000);
    });
  }

  async refresh(): Promise<void> {
    if (!this.auth.hasToken()) {
      this.raw.set([]);
      this.error.set(false);
      return;
    }

    // Scope travels in the `selectedOrganizationId` header (auth interceptor);
    // this is only recorded so a later workspace switch triggers one reload.
    const orgId = this.activeOrgId();
    this.loading.set(true);
    this.error.set(false);

    try {
      const list = await firstValueFrom(
        this.api.getAll().pipe(catchError(() => of(null))),
      );
      if (list === null) {
        this.error.set(true);
        this.raw.set([]);
        return;
      }
      this.loadedForOrgId = orgId;

      const readIds = this.loadReadIds();
      const mapped = list
        .map((dto) => this.mapDto(dto, readIds))
        .sort((a, b) => {
          const at = a.createdAt ? Date.parse(a.createdAt) : 0;
          const bt = b.createdAt ? Date.parse(b.createdAt) : 0;
          return bt - at;
        });
      this.raw.set(mapped);
    } finally {
      this.loading.set(false);
    }
  }

  markRead(id: number): void {
    this.raw.update((list) =>
      list.map((n) => (n.id === id ? { ...n, unread: false } : n)),
    );
    this.persistReadIds(
      new Set([...this.loadReadIds(), id].filter((x) => x > 0)),
    );
  }

  /** Puts an item back in the unread pile — read state is client-side only. */
  markUnread(id: number): void {
    this.raw.update((list) =>
      list.map((n) => (n.id === id ? { ...n, unread: true } : n)),
    );
    const ids = this.loadReadIds();
    ids.delete(id);
    this.persistReadIds(ids);
  }

  markAllRead(): void {
    const ids = this.raw()
      .map((n) => n.id)
      .filter((id) => id > 0);
    this.raw.update((list) => list.map((n) => ({ ...n, unread: false })));
    this.persistReadIds(new Set([...this.loadReadIds(), ...ids]));
  }

  /** Active workspace id, or null when none is selected (unscoped inbox). */
  private activeOrgId(): number | null {
    const id = Number(this.workspace.currentWorkspace()?.id ?? 0);
    return id > 0 ? id : null;
  }

  /** Rows the bell and inbox actually show — login greetings are omitted. */
  private inboxRaw(): RawNotification[] {
    return this.raw().filter(
      (n) => !isLoginWelcomeNotification(n.title, n.message),
    );
  }

  /** A live push belongs here when it is platform-wide or from this workspace. */
  private belongsToActiveWorkspace(organizationId: number | null): boolean {
    const active = this.activeOrgId();
    return active === null || organizationId === null || organizationId === active;
  }

  private prependLive(live: NotificationLiveDto): void {
    const readIds = this.loadReadIds();
    const item = this.mapLive(live, readIds);
    if (isLoginWelcomeNotification(item.title, item.message)) return;
    if (!this.belongsToActiveWorkspace(item.organizationId)) return;
    this.raw.update((list) => {
      if (item.id > 0 && list.some((n) => n.id === item.id)) {
        return list;
      }
      return [item, ...list];
    });
  }

  /** Renders one stored notification in the active language. */
  private present(n: RawNotification): AppNotificationItem {
    const category = classifyNotification(n.title, n.message, n.conversationId);
    const visual = visualFor(n.type, category);
    const body = this.backendI18n.translateMessage(n.message);
    return {
      id: n.id,
      icon: visual.icon,
      iconClass: visual.iconClass,
      title:
        this.backendI18n.translateMessage(n.title) ||
        this.translate.instant('notifications.meta.fallbackTitle'),
      preview: body,
      time: this.formatRelativeTime(n.createdAt),
      unread: n.unread,
      category,
      detailType: this.detailLabelForType(n.type),
      detailTime: this.formatDetailTime(n.createdAt),
      body,
      createdAt: n.createdAt,
      type: n.type,
      organizationId: n.organizationId,
      rawTitle: n.title,
      conversationId: n.conversationId,
    };
  }

  private mapDto(
    dto: NotificationOutDto,
    readIds: Set<number>,
  ): RawNotification {
    return {
      id: dto.id,
      title: dto.title ?? '',
      message: dto.message ?? '',
      createdAt: dto.createdAt ?? null,
      type: normalizeType(dto.type),
      unread: dto.id > 0 ? !readIds.has(dto.id) : true,
      organizationId: dto.organizationId ?? null,
      conversationId: dto.conversationId ?? null,
    };
  }

  private mapLive(
    live: NotificationLiveDto,
    readIds: Set<number>,
  ): RawNotification {
    const id =
      typeof live.id === 'number' && live.id > 0 ? live.id : this.tempIdSeq--;
    return {
      id,
      title: live.title ?? '',
      message: live.message ?? '',
      createdAt: live.createdAt ?? new Date().toISOString(),
      type: normalizeType(live.type),
      unread: id > 0 ? !readIds.has(id) : true,
      organizationId: live.organizationId ?? null,
      conversationId: live.conversationId ?? null,
    };
  }

  private detailLabelForType(type: NotificationType): string {
    switch (type) {
      case 'Success':
        return this.translate.instant('notifications.meta.typeSuccess');
      case 'Warning':
        return this.translate.instant('notifications.meta.typeWarning');
      case 'Error':
        return this.translate.instant('notifications.meta.typeAlert');
      case 'Info':
      default:
        return this.translate.instant('notifications.meta.typeUpdate');
    }
  }

  private formatRelativeTime(iso: string | null): string {
    if (!iso) return '';
    const then = Date.parse(iso);
    if (Number.isNaN(then)) return '';
    const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (seconds < 60) {
      return this.translate.instant('notifications.meta.justNow');
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return this.translate.instant('notifications.meta.minutesAgo', {
        count: minutes,
      });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return this.translate.instant('notifications.meta.hoursAgo', {
        count: hours,
      });
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return this.translate.instant('notifications.meta.daysAgo', {
        count: days,
      });
    }
    return new Date(then).toLocaleDateString(this.activeLocale());
  }

  /** Active UI language, or the browser default when none is resolved yet. */
  private activeLocale(): string | undefined {
    return this.translate.getCurrentLang() ?? undefined;
  }

  private formatDetailTime(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const relative = this.formatRelativeTime(iso);
    return `${d.toLocaleString(this.activeLocale())} · ${relative}`;
  }

  private readStorageKey(): string {
    const userId = this.auth.currentUser()?.id ?? 'anon';
    return `${READ_KEY_PREFIX}${userId}`;
  }

  private loadReadIds(): Set<number> {
    try {
      const raw = localStorage.getItem(this.readStorageKey());
      if (!raw) return new Set();
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return new Set();
      return new Set(
        parsed.filter((x): x is number => typeof x === 'number' && x > 0),
      );
    } catch {
      return new Set();
    }
  }

  private persistReadIds(ids: Set<number>): void {
    localStorage.setItem(this.readStorageKey(), JSON.stringify([...ids]));
  }
}

function normalizeType(type: NotificationType | number | undefined): NotificationType {
  if (typeof type === 'string') {
    if (
      type === 'Success' ||
      type === 'Info' ||
      type === 'Warning' ||
      type === 'Error'
    ) {
      return type;
    }
  }
  if (typeof type === 'number') {
    return (['Success', 'Info', 'Warning', 'Error'] as const)[type] ?? 'Info';
  }
  return 'Info';
}

/**
 * Severity picks the colour; the subject picks the glyph — except for warnings
 * and errors, where the alert glyph carries more meaning than the subject does.
 */
function visualFor(
  type: NotificationType,
  category: NotifCategory,
): { icon: string; iconClass: NotifIconClass } {
  switch (type) {
    case 'Warning':
      return { icon: '⚠️', iconClass: 'alert' };
    case 'Error':
      return { icon: '✕', iconClass: 'alert' };
    case 'Success':
      return { icon: categoryIcon(category), iconClass: 'success' };
    case 'Info':
    default:
      return { icon: categoryIcon(category), iconClass: 'system' };
  }
}
